// src/Components/Activities/AddActivityForm.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { skillsOptions } from "./activityData";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import DropDownField from "../../DropDownField";
import MultiDropDownField from "../../MultiDropDown";
import apiEndpoints from "../../apiconfig";

const baseForm = {
  branch_guid: "",
  class_guid: "",
  staff_guid: "",
  title: "",
  description: "",
  activity_date: "",
  images: [],
  skills: [],
  student_guids: [],
};

const safeParseJSON = (val, fallback) => {
  try {
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (!trimmed) return fallback;
      return JSON.parse(trimmed);
    }
    return Array.isArray(val) ? val : fallback;
  } catch {
    // If parse fails, maybe it's comma separated
    if (typeof val === "string") {
      return val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return fallback;
  }
};

const AddActivityForm = ({
  mode = "add",
  initialData,
  onSubmit,
  onCancel,
  readOnly,
  loggedUser,
}) => {
  const [form, setForm] = useState(baseForm);
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isDropdownsLoaded, setIsDropdownsLoaded] = useState(false);
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Normalizer: return object with { guid, name }
  const normalize = (item) => ({
    guid: item.guid,
    name:
      item.name || item.branch_name || item.class_name || item.staff_name || "",
  });

  // Fetch branches (normalized)
  const fetchBranches = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${apiEndpoints.dropdown}?type=branch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setBranchList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchBranches error", e);
    }
    return [];
  };

  // Fetch classes for a branch (normalized)
  const fetchClasses = async (branchGuid) => {
    if (!branchGuid) return [];
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `${apiEndpoints.dropdown}?type=class&branch_guid=${branchGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setClassList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchClasses error", e);
    }
    return [];
  };

  // Fetch staff for a branch (normalized)
  const fetchStaff = async (branchGuid) => {
    if (!branchGuid) return [];
    if (loggedUser.role === "staff") return [];
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `${apiEndpoints.dropdown}?type=staff&branch_guid=${branchGuid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.status === "success" && Array.isArray(data.data)) {
        const normalized = data.data.map(normalize);
        setStaffList(normalized);
        return normalized;
      }
    } catch (e) {
      console.error("fetchStaff error", e);
    }
    return [];
  };

  // Ensure list contains an option for given guid; if not, inject fallback
  const ensureOptionInListEnhanced = (list, guid, fallbackName) => {
    if (!guid) return list;
    const found = list.find((i) => String(i.guid) === String(guid));
    if (found) return list;
    return [{ guid, name: fallbackName || "Unknown" }, ...list];
  };

  // Load data for both create & edit, hydrate form after lists ready
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setIsDropdownsLoaded(false);

      // always fetch branches first
      const branches = await fetchBranches();

      if (!initialData) {
        // CREATE mode: auto-select branch for staff role
        if (loggedUser.role === "staff" && branches.length > 0) {
          const staffBranch = branches[0].guid;
          setForm((f) => ({
            ...baseForm,
            branch_guid: staffBranch,
            staff_guid: loggedUser.id,
          }));
          await fetchClasses(staffBranch);
          await fetchStaff(staffBranch);
        }
        if (mounted) setIsDropdownsLoaded(true);
        return;
      }

      // EDIT mode:
      const branchGuid = initialData.branch_guid;
      const classes = await fetchClasses(branchGuid);
      const staffs = await fetchStaff(branchGuid);

      const branchNameFallback = initialData.branch_name || "";
      const classNameFallback = initialData.class_name || "";
      const staffNameFallback = initialData.staff_name || "";

      const finalBranchList = ensureOptionInListEnhanced(
        branches,
        initialData.branch_guid,
        branchNameFallback
      );
      const finalClassList = ensureOptionInListEnhanced(
        classes,
        initialData.class_guid,
        classNameFallback
      );
      const finalStaffList = ensureOptionInListEnhanced(
        staffs,
        initialData.staff_guid,
        staffNameFallback
      );

      if (mounted) {
        setBranchList(finalBranchList);
        setClassList(finalClassList);
        setStaffList(finalStaffList);
      }

      // parse skills, student_guids, and images safely
      const skillsParsed = safeParseJSON(initialData.skills, []);
      const imagesParsed = Array.isArray(initialData.images)
        ? initialData.images
        : Array.isArray(initialData.photos)
        ? initialData.photos
        : [];
      const studentGuidsParsed = safeParseJSON(initialData.student_guids, []);

      // hydrate the form AFTER lists are ready
      if (mounted) {
        const newForm = {
          ...baseForm,
          ...initialData,
          branch_guid: initialData.branch_guid || "",
          class_guid: initialData.class_guid || "",
          staff_guid: initialData.staff_guid || "",
          skills: Array.isArray(skillsParsed) ? skillsParsed : [],
          images: imagesParsed,
          student_guids: Array.isArray(studentGuidsParsed) ? studentGuidsParsed : [],
          activity_date: initialData.activity_date || initialData.date || "",
          title: initialData.title || "",
          description: initialData.description || "",
        };
        
        setForm(newForm);
        setIsDropdownsLoaded(true);
      }
    };

    load();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Fetch class students list when class changes
  useEffect(() => {
    if (form.branch_guid && form.class_guid) {
      setLoadingStudents(true);
      const token = localStorage.getItem("token");
      fetch(
        `${apiEndpoints.activityStudents}?branch_guid=${form.branch_guid}&class_guid=${form.class_guid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success" && Array.isArray(data.data)) {
            setStudentsInClass(data.data);
            
            // If in CREATE mode and student_guids is empty, default to select all students
            if (mode === "add" && (!form.student_guids || form.student_guids.length === 0)) {
              handleChange("student_guids", data.data.map((s) => s.guid));
            }
          }
        })
        .catch((err) => console.error("Error fetching students:", err))
        .finally(() => setLoadingStudents(false));
    } else {
      setStudentsInClass([]);
    }
  }, [form.branch_guid, form.class_guid, mode]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setForm((prev) => ({
          ...prev,
          images: [...(prev.images || []), base64String],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    const newErrors = {
      title: !form.title,
      description: !form.description,
      activity_date: !form.activity_date,
      staff_guid: !form.staff_guid,
      branch_guid: !form.branch_guid,
      class_guid: !form.class_guid,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      showSnackbar("Please fill all required fields", "error");
      return;
    }

    const payload = {
      ...initialData,
      ...form,
      staff_guid: loggedUser.role === "staff" ? loggedUser.id : form.staff_guid,
      images: form.images || [],
      skills: form.skills,
      student_guids: form.student_guids || [],
      activity_date: form.activity_date,
    };

    onSubmit?.(payload);
    showSnackbar(
      mode === "add"
        ? "Activity created successfully!"
        : "Activity updated successfully!",
      "success"
    );
  };

  if (!isDropdownsLoaded) {
    return <div>Loading…</div>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        🎨 {mode === "add" ? "Add New Activity" : "Edit Activity"}
      </Typography>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Fill in the activity details below. Required fields are marked with *.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Activity Title *"
            fullWidth
            disabled={readOnly}
            value={form.title || ""}
            error={!!errors.title}
            onChange={(e) => handleChange("title", e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <DropDownField
            label="Branch *"
            value={form.branch_guid}
            disabled={readOnly}
            options={(() => {
              const currentGuid = form.branch_guid;
              const inList = branchList.some(
                (b) => String(b.guid) === String(currentGuid)
              );

              if (currentGuid && !inList && initialData?.branch_name) {
                return [
                  {
                    guid: currentGuid,
                    name: `${initialData.branch_name} (current)`,
                  },
                  ...branchList,
                ];
              }
              return branchList;
            })()}
            error={!!errors.branch_guid}
            onChange={async (guid) => {
              handleChange("branch_guid", guid);
              handleChange("class_guid", "");
              handleChange("staff_guid", "");
              await fetchClasses(guid);
              await fetchStaff(guid);
            }}
          />

          <DropDownField
            label="Class *"
            value={form.class_guid}
            disabled={readOnly}
            options={(() => {
              const currentGuid = form.class_guid;
              const inList = classList.some(
                (c) => String(c.guid) === String(currentGuid)
              );

              if (currentGuid && !inList && initialData?.class_name) {
                return [
                  {
                    guid: currentGuid,
                    name: `${initialData.class_name} (current)`,
                  },
                  ...classList,
                ];
              }
              return classList;
            })()}
            error={!!errors.class_guid}
            onChange={(guid) => {
              handleChange("class_guid", guid);
              handleChange("student_guids", []);
            }}
          />

          {loggedUser.role === "admin" ? (
            <DropDownField
              label="Staff *"
              disabled={readOnly}
              value={form.staff_guid}
              options={(() => {
                const currentGuid = form.staff_guid;
                const inList = staffList.some(
                  (s) => String(s.guid) === String(currentGuid)
                );

                if (currentGuid && !inList && initialData?.staff_name) {
                  return [
                    {
                      guid: currentGuid,
                      name: `${initialData.staff_name} (current)`,
                    },
                    ...staffList,
                  ];
                }
                return staffList;
              })()}
              error={!!errors.staff_guid}
              onChange={(guid) => handleChange("staff_guid", guid)}
            />
          ) : (
            <TextField
              label="Staff"
              fullWidth
              disabled={readOnly}
              value={loggedUser.name}
              sx={{ mb: 3 }}
              helperText="Auto-assigned to logged-in staff"
            />
          )}

          <MultiDropDownField
            label="Skills Developed"
            value={Array.isArray(form.skills) ? form.skills : []}
            options={skillsOptions}
            onChange={(selected) => handleChange("skills", selected)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Description *"
            fullWidth
            multiline
            disabled={readOnly}
            minRows={3}
            value={form.description || ""}
            error={!!errors.description}
            onChange={(e) => handleChange("description", e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          <TextField
            label="Activity Date *"
            type="date"
            fullWidth
            disabled={readOnly}
            value={form.activity_date || ""}
            error={!!errors.activity_date}
            onChange={(e) => handleChange("activity_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 3 }}
            InputProps={{ sx: { borderRadius: 3 } }}
          />

          {/* Student Checkbox List */}
          {form.class_guid && (
            <Box
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Select Students *
              </Typography>
              {loadingStudents ? (
                <Typography variant="body2">Loading students...</Typography>
              ) : studentsInClass.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No students found in this class.
                </Typography>
              ) : (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={readOnly}
                        checked={
                          studentsInClass.length > 0 &&
                          (form.student_guids || []).length === studentsInClass.length
                        }
                        indeterminate={
                          (form.student_guids || []).length > 0 &&
                          (form.student_guids || []).length < studentsInClass.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleChange(
                              "student_guids",
                              studentsInClass.map((s) => s.guid)
                            );
                          } else {
                            handleChange("student_guids", []);
                          }
                        }}
                      />
                    }
                    label={<span style={{ fontWeight: 600 }}>Select All</span>}
                  />
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                    <FormGroup>
                      {studentsInClass.map((student) => {
                        const isChecked = (form.student_guids || []).includes(
                          student.guid
                        );
                        return (
                          <FormControlLabel
                            key={student.guid}
                            control={
                              <Checkbox
                                disabled={readOnly}
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleChange("student_guids", [
                                      ...(form.student_guids || []),
                                      student.guid,
                                    ]);
                                  } else {
                                    handleChange(
                                      "student_guids",
                                      (form.student_guids || []).filter(
                                        (id) => id !== student.guid
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={`${student.first_name} ${
                              student.last_name || ""
                            }`}
                          />
                        );
                      })}
                    </FormGroup>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Multiple Image Upload Component */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Activity Images
            </Typography>
            
            <Button
              variant="outlined"
              component="label"
              disabled={readOnly}
              sx={{ mb: 2, borderRadius: 3, textTransform: "none" }}
            >
              Upload Images
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(form.images || []).map((img, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <img
                    src={img}
                    alt={`uploaded-${index}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {!readOnly && (
                    <Button
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        minWidth: "auto",
                        width: 20,
                        height: 20,
                        p: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                        fontSize: 10,
                      }}
                    >
                      ✕
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "end", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ mb: 2, borderRadius: 999, px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ mb: 2, borderRadius: 999, px: 3 }}
        >
          {mode === "add" ? "Create Activity" : "Update Activity"}
        </Button>
      </Box>
    </Box>
  );
};

export default AddActivityForm;